import { firebaseClient } from "../firebaseClient";
import * as Sentry from "@sentry/nextjs";
import { DNDType } from "../components/kanban/types";
import { getAccount } from "../utils/sessionHelper";
import { v4 as uuidv4 } from 'uuid';


export const saveKanbanData = async (session, columns: DNDType[]) => {
  const db = firebaseClient.firestore();
  const userId = getAccount(session)?.id;
  const accountId = getAccount(session)?.idAccount;

  if (!userId || !accountId) return;

  try {
    await db.collection("users").doc(userId).collection("kanban").doc("board").update({
      columns: columns.map(col => ({
        id: col.id,
        title: col.title,
        color: col.color,
        items: col.items,
        isVisible: col.isVisible ?? true
      })),
      lastUpdated: new Date()
    });
  } catch (e) {
    console.error("Error saving kanban data:", e);
    Sentry.captureException(e);
  }
};

export const subscribeToKanbanData = (
  session,
  onUpdate: (columns: DNDType[]) => void,
  onError?: (error: Error) => void
) => {
  const db = firebaseClient.firestore();
  const userId = getAccount(session)?.id;
  const accountId = getAccount(session)?.idAccount;

  if (!userId || !accountId) return () => { };

  try {
    return db.collection("users")
      .doc(userId)
      .collection("kanban")
      .doc("board")
      .onSnapshot(
        (snapshot) => {
          const columns: DNDType[] = snapshot.exists ? (snapshot.data()?.columns || []) : [];
          onUpdate(columns);
        },
        (error) => {
          console.error("Error in kanban subscription:", error);
          Sentry.captureException(error);
          onError?.(error);
        }
      );
  } catch (e) {
    console.error("Error setting up kanban subscription:", e);
    Sentry.captureException(e);
    onError?.(e as Error);
    return () => { };
  }
};

export const subscribeToKanbanDrafts = (
  session,
  status: "draft" | "scheduled",
  onUpdate: (drafts: any[]) => void,
  onError?: (error: Error) => void
) => {
  const db = firebaseClient.firestore();
  const userId = getAccount(session)?.id;
  const accountId = getAccount(session)?.idAccount;

  if (!userId || !accountId) return () => { };

  try {
    return db.collection("users")
      .doc(userId)
      .collection("tweets")
      .where("status", "==", status)
      .where("idAccount", "==", accountId)
      .orderBy("dateCreated", "desc")
      .onSnapshot(
        (snapshot) => {
          const drafts: any[] = [];
          snapshot.forEach(doc => {
            const data = doc.data();
            if (data.text !== "") {
              drafts.push(data);
            }
          });
          onUpdate(drafts);
        },
        (error) => {
          console.error(`Error in ${status} subscription:`, error);
          Sentry.captureException(error);
          onError?.(error);
        }
      );
  } catch (e) {
    console.error(`Error setting up ${status} subscription:`, e);
    Sentry.captureException(e);
    onError?.(e as Error);
    return () => { };
  }
};

export const deleteKanbanPost = async (session, tweetId: string) => {
  const db = firebaseClient.firestore();
  const userId = getAccount(session)?.id;
  const accountId = getAccount(session)?.idAccount;

  if (!userId || !accountId) return;

  try {
    await db
      .collection("users")
      .doc(userId)
      .collection("tweets")
      .doc(tweetId)
      .delete();
  } catch (e) {
    console.error("Error deleting kanban post:", e);
    Sentry.captureException(e);
  }
};

export const setDefaultColumns = async (session) => {
  const db = firebaseClient.firestore();
  const userId = getAccount(session)?.id;

  if (!userId) return;

  try {
    const boardRef = db.collection("users").doc(userId).collection("kanban").doc("board");
    const boardDoc = await boardRef.get();

    if (!boardDoc.exists || boardDoc.data()?.isDefaultColumnsSet !== true) {
      const defaultColumns = [
        { id: uuidv4(), title: "In progress", color: "yellow", items: [], isVisible: true },
        { id: uuidv4(), title: "Ready to post", color: "green", items: [], isVisible: true },
      ]
      const existingColumns = boardDoc.exists ? boardDoc.data()?.columns || [] : [];
      const columns = [...defaultColumns, ...existingColumns];
      await boardRef.set({
        isDefaultColumnsSet: true,
        lastUpdated: new Date(),
        columns
      }, { merge: true });
    }
  } catch (e) {
    console.error("Error setting kanban first access:", e);
    Sentry.captureException(e);
  }
};
